import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Download, Upload, LogOut, FileText, ShieldCheck, Trash2, Layers, Lock } from 'lucide-react';
import api from '../api';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { COMMODITIES, commodityLabel } from '../constants/commodities';
import { useLang } from '../i18n/LanguageContext';

const CATEGORY_ICONS = {
  loi: FileText, icpo: FileText, banking: ShieldCheck,
  passport: ShieldCheck, license: ShieldCheck, other: FileText,
};

function formatSize(b) {
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
}

export default function ExtranetPage() {
  const { user, logout, isAdmin } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const { toast } = useToast();
  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', file: null, commodity: '' });
  const [activeCommodity, setActiveCommodity] = useState(null);

  const load = async () => {
    try {
      const [m, d, u] = await Promise.all([
        api.get('/auth/me'),
        api.get('/extranet/documents'),
        api.get('/extranet/uploads'),
      ]);
      setMe(m.data);
      setDocs(d.data || []);
      setUploads(u.data || []);
      const commodities = (m.data.commodities || []);
      if (commodities.length && !activeCommodity) setActiveCommodity(commodities[0]);
      if (commodities.length && !form.commodity) setForm((f) => ({ ...f, commodity: commodities[0] }));
    } catch (err) {
      // Only redirect to login if the user is still authenticated in storage
      // (avoids race with logout button which clears auth then navigates home).
      if (err?.response?.status === 401 && localStorage.getItem('yuville_auth')) {
        logout();
        nav('/extranet/login');
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (doc) => {
    try {
      const res = await api.get(`/extranet/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: t('ext.downloadErr') });
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!form.file || !form.title) {
      toast({ title: t('ext.requiredFields'), description: t('ext.requiredFieldsDesc') });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title);
      fd.append('commodity', form.commodity || activeCommodity || 'autre');
      fd.append('description', form.description);
      await api.post('/extranet/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: t('ext.successTitle'), description: t('ext.successDesc') });
      setForm({ title: '', description: '', file: null, commodity: activeCommodity || '' });
      load();
    } catch (err) {
      toast({ title: t('ext.errorTitle'), description: err?.response?.data?.detail || t('ext.uploadError') });
    } finally { setUploading(false); }
  };

  const deleteMine = async (id) => {
    if (!window.confirm(t('ext.confirmDelete'))) return;
    await api.delete(`/extranet/uploads/${id}`);
    load();
  };

  const commodities = me?.commodities || [];
  const docsInCommodity = docs.filter((d) => d.commodity === activeCommodity);
  const uploadsInCommodity = uploads.filter((u) => u.commodity === activeCommodity);

  // Group current commodity docs by category
  const grouped = {};
  ['loi', 'icpo', 'banking', 'passport', 'license', 'other'].forEach((c) => (grouped[c] = []));
  docsInCommodity.forEach((d) => { grouped[d.category] = grouped[d.category] || []; grouped[d.category].push(d); });

  if (me && commodities.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header onCTA={() => nav('/')} />
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-yellow/15 border border-brand-yellow mb-6">
              <Lock className="w-7 h-7 text-brand-navy" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-brand-navy mb-3">{t('ext.noCommodityTitle')}</h1>
            <p className="text-gray-600 mb-6">{t('ext.noCommodityDesc')} <a href="mailto:denis@yuville.com" className="text-brand-navy font-semibold underline">denis@yuville.com</a> {t('ext.noCommodityDesc2')}</p>
            <button onClick={() => { logout(); window.location.assign('/'); }} className="btn-yellow"><LogOut className="w-4 h-4" /> {t('ext.logout')}</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const CATEGORY_LABELS = {
    loi: t('ext.catLoi'), icpo: t('ext.catIcpo'), banking: t('ext.catBanking'),
    passport: t('ext.catPassport'), license: t('ext.catLicense'), other: t('ext.catOther'),
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header onCTA={() => nav('/')} />

      <main className="flex-1 pt-28 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <div data-testid="extranet-private-space" className="text-xs text-brand-yellow font-bold tracking-widest uppercase mb-1">{t('ext.privateSpace')}</div>
              <h1 data-testid="extranet-welcome" className="text-3xl md:text-4xl font-bold text-brand-navy">{t('ext.welcome')}, {me?.displayName || me?.username}</h1>
              <p className="text-gray-600 mt-2">{t('ext.yourCommodities')} <span className="font-semibold text-brand-navy">{commodities.map((c) => commodityLabel(c, lang)).join(' · ')}</span></p>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && <Link to="/admin" className="px-4 py-2 rounded bg-brand-navy text-white text-sm hover:bg-[#070d33] transition">{t('ext.admin')}</Link>}
              <button data-testid="extranet-logout-button" onClick={() => { logout(); window.location.assign('/'); }} className="inline-flex items-center gap-2 px-4 py-2 rounded border border-gray-300 text-brand-navy text-sm hover:border-brand-yellow transition">
                <LogOut className="w-4 h-4" /> {t('ext.logout')}
              </button>
            </div>
          </div>

          {/* Commodity tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {commodities.map((cid) => {
              const active = cid === activeCommodity;
              return (
                <button key={cid} onClick={() => { setActiveCommodity(cid); setForm((f) => ({ ...f, commodity: cid })); }}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm border transition ${
                    active ? 'bg-brand-navy text-white border-brand-navy shadow-lg' : 'bg-white text-brand-navy border-gray-200 hover:border-brand-yellow'
                  }`}>
                  <Layers className="w-4 h-4" /> {t('ext.sectionPrefix')} {commodityLabel(cid, lang)}
                </button>
              );
            })}
          </div>

          {/* Documents in active commodity */}
          <div className="mb-4 flex items-center gap-2">
            <div className="w-2 h-8 bg-brand-yellow rounded-full" />
            <h2 className="text-xl font-bold text-brand-navy">{t('ext.documentsFor')} — {commodityLabel(activeCommodity, lang)}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {Object.entries(grouped).map(([cat, list]) => {
              const Icon = CATEGORY_ICONS[cat] || FileText;
              return (
                <div key={cat} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-brand-navy" />
                    </div>
                    <h3 className="font-bold text-brand-navy">{CATEGORY_LABELS[cat]}</h3>
                    <span className="ml-auto text-xs text-gray-500">{list.length}</span>
                  </div>
                  {list.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">{t('ext.noDocuments')}</p>
                  ) : (
                    <ul className="space-y-2">
                      {list.map((d) => (
                        <li key={d.id} className="flex items-center justify-between p-3 rounded border border-gray-100 hover:border-brand-yellow transition">
                          <div className="min-w-0">
                            <div className="font-medium text-brand-navy text-sm truncate flex items-center gap-2">
                              {d.title}
                              <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded uppercase font-bold">{d.owner === 'qianyan' ? 'Qianyan' : 'Yuville'}</span>
                            </div>
                            <div className="text-xs text-gray-500 truncate">{d.filename} • {formatSize(d.size)}</div>
                          </div>
                          <button onClick={() => download(d)} className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand-yellow text-brand-navy text-xs font-semibold hover:bg-[#ffdb52] transition">
                            <Download className="w-3.5 h-3.5" /> PDF
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* Upload zone */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-brand-navy flex items-center justify-center">
                <Upload className="w-5 h-5 text-brand-yellow" />
              </div>
              <h2 className="font-bold text-brand-navy">{t('ext.deposit')}</h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">{t('ext.depositIntro')} <strong>{commodityLabel(form.commodity || activeCommodity, lang)}</strong>. {t('ext.depositIntro2')}</p>
            <form onSubmit={submitUpload} className="grid md:grid-cols-2 gap-4">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('ext.titleFieldPh')}
                className="px-4 py-3 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 transition" />
              <select value={form.commodity} onChange={(e) => setForm({ ...form, commodity: e.target.value })}
                className="px-4 py-3 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow bg-white">
                {commodities.map((c) => <option key={c} value={c}>{commodityLabel(c, lang)}</option>)}
              </select>
              <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="md:col-span-2 px-4 py-2 border border-gray-200 rounded file:mr-3 file:border-0 file:bg-brand-navy file:text-white file:px-3 file:py-1.5 file:rounded file:text-xs file:font-semibold" />
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t('ext.descPh')}
                className="md:col-span-2 px-4 py-3 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow resize-none" />
              <button type="submit" disabled={uploading} className="md:col-span-2 btn-yellow justify-center disabled:opacity-60">
                <Upload className="w-4 h-4" /> {uploading ? t('ext.sending') : t('ext.depositBtn')}
              </button>
            </form>
          </div>

          {/* My uploads in this commodity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-bold text-brand-navy mb-4">{t('ext.myUploads')} — {commodityLabel(activeCommodity, lang)} ({uploadsInCommodity.length})</h2>
            {uploadsInCommodity.length === 0 ? (
              <p className="text-sm text-gray-400 italic">{t('ext.noUploads')}</p>
            ) : (
              <ul className="space-y-2">
                {uploadsInCommodity.map((u) => (
                  <li key={u.id} className="flex items-center justify-between p-3 rounded border border-gray-100">
                    <div className="min-w-0">
                      <div className="font-medium text-brand-navy text-sm truncate">{u.title}</div>
                      <div className="text-xs text-gray-500 truncate">{u.filename} • {formatSize(u.size)} • {new Date(u.createdAt).toLocaleString()}</div>
                    </div>
                    <button onClick={() => deleteMine(u.id)} className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-200 text-red-600 text-xs hover:border-red-500 transition">
                      <Trash2 className="w-3.5 h-3.5" /> {t('ext.delete')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
